import React from 'react';
import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import type { UsageResponse } from '../../services/billingService';
import {
  formatOverageEurAmount,
  overageUsagePercent,
  resolvedOverageCreditsLimit,
  resolvedOverageCreditsUsed,
  shouldShowOverageUsageMeter,
} from '../../helpers/overageLimitDisplay';

interface OverageUsageMeterProps {
  usage: UsageResponse;
  compact?: boolean;
  loading?: boolean;
}

const OverageUsageMeter: React.FC<OverageUsageMeterProps> = ({
  usage,
  compact = false,
  loading = false,
}) => {
  const { t } = useTranslation();
  if (!shouldShowOverageUsageMeter(usage)) return null;

  const usedCredits = resolvedOverageCreditsUsed(usage);
  const limitCredits = resolvedOverageCreditsLimit(usage);
  const hasCap = limitCredits != null && limitCredits > 0;
  const percent = hasCap ? overageUsagePercent(usage) : 0;
  const usedEur = formatOverageEurAmount(usedCredits);
  const limitEur = hasCap ? formatOverageEurAmount(limitCredits) : null;
  const barColor =
    percent >= 90 ? 'error' : percent >= 70 ? 'warning' : 'primary';
  const label = hasCap
    ? t('Overage usage euros capped', { used: usedEur, limit: limitEur })
    : t('Overage usage euros uncapped', { used: usedEur });

  return (
    <Box sx={{ mb: compact ? 1.5 : 0 }}>
      <Typography
        variant={compact ? 'caption' : 'subtitle2'}
        color={compact ? 'text.secondary' : 'text.primary'}
        display="block"
        fontWeight={compact ? 400 : 600}
        sx={{ mb: compact ? 0.5 : 1 }}
      >
        {t('Pay as you go overage')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: compact ? 'stretch' : 'center',
          gap: compact ? 0 : 2,
          flexWrap: 'wrap',
          flexDirection: compact ? 'column' : 'row',
        }}
      >
        {hasCap ? (
          <LinearProgress
            variant="determinate"
            value={loading ? 0 : percent}
            color={barColor}
            sx={{
              flex: 1,
              width: compact ? '100%' : undefined,
              minWidth: compact ? undefined : 160,
              height: compact ? 6 : 10,
              borderRadius: 1,
              mb: compact ? 0.5 : 0,
            }}
          />
        ) : null}
        {compact ? (
          <Typography variant="body2">
            {loading ? t('Loading...') : label}
          </Typography>
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={loading ? t('Loading...') : label}
            sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default OverageUsageMeter;
